import { DeepgramRealtimeAdvancedFields } from "@/components/shared/forms/DeepgramRealtimeFormFields"
import {
  GeminiRealtimeAdvancedFields,
  GrokRealtimeAdvancedFields,
  OpenAIRealtimeAdvancedFields,
  RealtimeAdvancedSettingsCard,
} from "@/components/shared/forms/RealtimeAdvancedSettingsFields"
import { SimulatorType } from "@/types/api/SimulatorType"
import type { SimulationMetadata } from "@/types/backend-contracts"
import type { RequiredKey } from "@/types/SimulatorConfig"
import {
  isOpenAIRealtimeApiKeySource,
  OPENAI_REALTIME_API_KEY_SOURCE,
} from "@/utils/simulation/simulatorFields"

import {
  jsonMetadataToEditableString,
  parseOptionalFloat,
  parseOptionalInt,
} from "../utils/agentPreviewMetadataFormatters"

interface AgentRealtimeAdvancedSettingsProps {
  simulatorType: SimulatorType
  simulationMetadata: SimulationMetadata
  requiredKeys: RequiredKey[]
  onMetadataUpdate: (update: Partial<SimulationMetadata>) => void
  onOpenAIRealtimeApiKeySourceChange: (value: string) => void
}

export function AgentRealtimeAdvancedSettings({
  simulatorType,
  simulationMetadata,
  requiredKeys,
  onMetadataUpdate,
  onOpenAIRealtimeApiKeySourceChange,
}: AgentRealtimeAdvancedSettingsProps) {
  if (simulatorType === SimulatorType.DEEPGRAM_REALTIME) {
    return (
      <RealtimeAdvancedSettingsCard>
        <DeepgramRealtimeAdvancedFields
          showInlineHelp={false}
          functions={jsonMetadataToEditableString(
            simulationMetadata.deepgram_realtime_functions,
          )}
          settings={jsonMetadataToEditableString(
            simulationMetadata.deepgram_realtime_settings,
          )}
          timeoutSeconds={parseOptionalInt(
            simulationMetadata.simulation_timeout_seconds,
          )}
          requiredFieldKeys={
            new Set(
              requiredKeys
                .filter(({ optional }) => optional !== true)
                .map(({ key }) => key),
            )
          }
          onFunctionsChange={(value) =>
            onMetadataUpdate({ deepgram_realtime_functions: value })
          }
          onSettingsChange={(value) =>
            onMetadataUpdate({ deepgram_realtime_settings: value })
          }
          onTimeoutSecondsChange={(value) =>
            onMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
          hidePlaceholders
        />
      </RealtimeAdvancedSettingsCard>
    )
  }

  if (simulatorType === SimulatorType.GEMINI_REALTIME) {
    return (
      <RealtimeAdvancedSettingsCard>
        <GeminiRealtimeAdvancedFields
          temperature={parseOptionalFloat(
            simulationMetadata.gemini_realtime_temperature,
          )}
          timeoutSeconds={parseOptionalInt(
            simulationMetadata.simulation_timeout_seconds,
          )}
          onTemperatureChange={(value) =>
            onMetadataUpdate({
              gemini_realtime_temperature:
                value !== undefined ? value.toString() : "",
            })
          }
          onTimeoutSecondsChange={(value) =>
            onMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
        />
      </RealtimeAdvancedSettingsCard>
    )
  }

  if (simulatorType === SimulatorType.GROK_REALTIME) {
    return (
      <RealtimeAdvancedSettingsCard>
        <GrokRealtimeAdvancedFields
          timeoutSeconds={parseOptionalInt(
            simulationMetadata.simulation_timeout_seconds,
          )}
          onTimeoutSecondsChange={(value) =>
            onMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
        />
      </RealtimeAdvancedSettingsCard>
    )
  }

  if (simulatorType === SimulatorType.OPENAI_REALTIME) {
    return (
      <RealtimeAdvancedSettingsCard>
        <OpenAIRealtimeAdvancedFields
          apiKey={(simulationMetadata.openai_realtime_api_key as string) || ""}
          apiKeySource={
            isOpenAIRealtimeApiKeySource(
              simulationMetadata.openai_realtime_api_key_source,
            )
              ? simulationMetadata.openai_realtime_api_key_source
              : OPENAI_REALTIME_API_KEY_SOURCE.CUSTOMER_PROVIDED
          }
          model={(simulationMetadata.openai_realtime_model as string) || ""}
          webSearchEnabled={
            simulationMetadata.openai_realtime_web_search_enabled === true ||
            simulationMetadata.openai_realtime_web_search_enabled === "true"
          }
          temperature={parseOptionalFloat(
            simulationMetadata.openai_realtime_temperature,
          )}
          timeoutSeconds={parseOptionalInt(
            simulationMetadata.simulation_timeout_seconds,
          )}
          onApiKeyChange={(value) =>
            onMetadataUpdate({ openai_realtime_api_key: value })
          }
          onApiKeySourceChange={onOpenAIRealtimeApiKeySourceChange}
          onWebSearchEnabledChange={(value) =>
            onMetadataUpdate({ openai_realtime_web_search_enabled: value })
          }
          onTemperatureChange={(value) =>
            onMetadataUpdate({
              openai_realtime_temperature:
                value !== undefined ? value.toString() : "",
            })
          }
          onTimeoutSecondsChange={(value) =>
            onMetadataUpdate({
              simulation_timeout_seconds:
                value !== undefined ? value.toString() : "",
            })
          }
        />
      </RealtimeAdvancedSettingsCard>
    )
  }

  return null
}
